import { motion } from "framer-motion";
import ProcessingDataAnimation from "./utils/ProcessingDataAnimation";
import { StudentImportListType } from "utils/interfaces";
import { usePlatform } from "components/utils/PlatformContext";

interface ImportCompleteProps {
  existingStudents: StudentImportListType[];
}

const ImportComplete: React.FC<ImportCompleteProps> = ({
  existingStudents,
}) => {
  // Get the platform context
  const { dataRequirements } = usePlatform();
  
  /**
   * Function executed when clicking the download button
   * for the previous version of the database
   */
  const handleDownloadBackup = () => {
    // Define the array that will contain the csv file
    const csvHeader =
      dataRequirements.includes("heritage")
        ? ["First name,Last name,DOB,Gender,Class name,Aboriginal or Torres Strait Islander orgin (Y/N),User name,Notes"]
        : ["First name,Last name,DOB,Gender,Class name,User name,Notes"];

    // Transform each existing student into a string line
    const contentLines = existingStudents.map(
      (student: StudentImportListType) => {
        // Define the output array
        const array =
          dataRequirements.includes("heritage")
            ? [
              student.firstName,
              student.lastName,
              student.dateOfBirth,
              student.gender,
              (student.classes as any).name,
              student.heritage,
              student.username,
              student.notes,
            ]
            : [
                student.firstName,
                student.lastName,
                student.dateOfBirth,
                student.gender,
                (student.classes as any).name,
                student.username,
                student.notes,
              ];

        return array.join(",");
      }
    );

    // Define the output file
    const csvFile = [...csvHeader, ...contentLines];

    // And transform it into a string
    const csvFileString = csvFile.join("\n");

    // Create a Blob with the CSV data
    const blob = new Blob([csvFileString], { type: "text/csv;charset=utf-8;" });

    // Create a link element
    const link = document.createElement("a");

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Set link attributes
    link.href = url;
    link.download = "students-backup.csv";

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // And finally revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.1, ease: "easeOut" }}
      className="self-stretch px-8 pt-8 w-[437px] flex-col justify-between items-center inline-flex overflow-y-auto overflow-x-hidden custom-scroll"
    >
      <div className="w-[373px] opacity-0">
        <span className="text-neutral-700 text-sm font-normal font-sans leading-relaxed">
          Would you like to download a backup of your users and groups before we
          applied the changes?
        </span>
        <span className="text-text-accent-600 text-sm font-normal font-sans leading-relaxed">
          Click here
        </span>
      </div>
      <ProcessingDataAnimation
        finishedValidation={true}
        processingText="Import complete"
      />
      <div className="self-stretch justify-start items-center gap-0.5 inline-flex">
        <div className="grow shrink basis-0">
          <span className="text-neutral-700 text-sm font-normal font-sans leading-relaxed select-none">
            Would you like to download a backup of your students and classes
            before these changes were applied?
          </span>
          <br className="select-none" />
          <span
            className="text-accent-600 hover:text-accent-700 text-sm font-normal font-sans underline leading-relaxed cursor-pointer select-none"
            onClick={handleDownloadBackup}
          >
            Click here to download
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ImportComplete;
